import { useMemo } from 'react'

import type { Campus, OrderStatus } from '@/lib/chefs/types'

export interface CampusOrderState {
  status: OrderStatus
  deadlineLabel: string
  daysOverdue: number
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function formatDeadline(deadline: Date): string {
  const dayName = DAY_NAMES[deadline.getDay()]
  let hours = deadline.getHours()
  const meridiem = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12 || 12
  const minutes = deadline.getMinutes()
  const time = minutes === 0 ? `${hours}${meridiem}` : `${hours}:${String(minutes).padStart(2, '0')}${meridiem}`
  return `${dayName} at ${time}`
}

export function computeCampusOrderState(campus: Campus, today: Date, isFutureRange: boolean): CampusOrderState {
  const deadline = new Date(campus.orderStatus.deadline)
  const deadlineLabel = formatDeadline(deadline)
  const submitted = campus.orderStatus.submittedAt != null

  if (submitted) {
    return { status: 'submitted', deadlineLabel, daysOverdue: 0 }
  }
  // Future ranges (or deadlines still in the future) collapse to 'pending' —
  // no missed-deadline warning fires.
  if (isFutureRange || deadline.getTime() > today.getTime()) {
    return { status: 'pending', deadlineLabel, daysOverdue: 0 }
  }
  const msPerDay = 24 * 60 * 60 * 1000
  const daysOverdue = Math.max(1, Math.floor((today.getTime() - deadline.getTime()) / msPerDay))
  return { status: 'missed', deadlineLabel, daysOverdue }
}

export function useCampusOrderStates(
  campuses: Campus[],
  today: Date,
  isFutureRange: boolean,
): Map<string, CampusOrderState> {
  return useMemo(() => {
    const map = new Map<string, CampusOrderState>()
    for (const campus of campuses) {
      map.set(campus.id, computeCampusOrderState(campus, today, isFutureRange))
    }
    return map
  }, [campuses, today, isFutureRange])
}
